// client/src/pages/EstimatePage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import PartsUploader  from '../components/PartsUploader';
import TableManager   from '../components/TableManager';
import CostEstimator  from '../components/CostEstimator';
import api            from '../utils/api';
import { ProjectContext } from '../contexts/ProjectContext';
import { useOrg } from '../contexts/OrgContext';
import { toast } from 'react-toastify';

export default function EstimatePage() {
  const { projectId } = useParams();
  const { activeProject } = useContext(ProjectContext);
  const { permissions } = useOrg();

  const [partsList, setPartsList]       = useState(null);
  const [uploadLabel, setUploadLabel]   = useState('');
  const [loadedTable, setLoadedTable]   = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null); // <-- NEW: keep the real File/Blob
  const [manualMode, setManualMode]         = useState(false);
  const [manualTrigger, setManualTrigger]   = useState(0);
  const [laborProfiles, setLaborProfiles] = useState([]);
  const [laborLoading, setLaborLoading] = useState(false);
  const [laborError, setLaborError] = useState('');

  // trigger re-fetch of saved list after share/unshare
  const [savedRefresh, setSavedRefresh] = useState(0);
  const bumpSaved = () => setSavedRefresh((k) => k + 1);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    // optional: react to projectId here
  }, [projectId]);

  const beginManualEntry = () => {
    setManualMode(true);
    setManualTrigger((k) => k + 1);
    setLoadedTable(null);
    setUploadedFile(null);
    setPartsList([]);
    setUploadLabel('');
  };

  const laborEnabled = Boolean(permissions?.featureFlags?.labor);

  useEffect(() => {
    let cancelled = false;
    if (!laborEnabled) {
      setLaborProfiles([]);
      setLaborError('');
      return;
    }
    setLaborLoading(true);
    setLaborError('');
    api
      .get('/org/labor')
      .then(({ data }) => {
        if (cancelled) return;
        setLaborProfiles(data?.labor || []);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err?.response?.data?.error || err?.message || 'Unable to load labor roles.';
        console.error('[EstimatePage] labor fetch error', message);
        setLaborError(message);
      })
      .finally(() => {
        if (!cancelled) setLaborLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [laborEnabled]);

  // Handle upload from PartsUploader
  // CSV -> also build a simple item list from text (nice local preview)
  // XLSX -> do NOT read as text; just pass the file to CostEstimator (server converts)
  const handlePartsReady = (file) => {
    if (!file) return;
    setManualMode(false);
    setManualTrigger(0);

    const name = file.name || '';
    const lower = name.toLowerCase();
    const isCsv  = /\.csv$/i.test(lower);
    const isXlsx = /\.(xlsx|xlsm?)$/i.test(lower);

    setUploadLabel(name);
    setLoadedTable(null);
    setUploadedFile(file); // <-- always keep the real file so CostEstimator can send it

    if (isCsv) {
      const reader = new FileReader();
      reader.onload = () => {
        const text  = String(reader.result || '');
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const items = lines.map(item => ({ item, quantity: 1, unitCost: 0 }));
        setPartsList(items);
      };
      reader.onerror = () => {
        console.error('Failed to read CSV', reader.error);
        setPartsList([]);
      };
      reader.readAsText(file);
    } else if (isXlsx) {
      // XLSX: skip text parsing; server will convert + extract
      setPartsList([]); // keep UI path active; CostEstimator will use uploadedFile
    } else {
      // Unknown type: fall back to trying text, but don't block UI
      const reader = new FileReader();
      reader.onload = () => {
        const text  = String(reader.result || '');
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
        const items = lines.map(item => ({ item, quantity: 1, unitCost: 0 }));
        setPartsList(items);
      };
      reader.onerror = () => {
        console.error('Failed to read file', reader.error);
        setPartsList([]);
      };
      reader.readAsText(file);
    }
  };

  const handleLoad = (table) => {
    setLoadedTable(table);
    setManualMode(false);
    setManualTrigger(0);
    setUploadedFile(null); // <-- ensure we don't accidentally send a stale upload
    setPartsList(
      table.breakdown.map(r => ({
        item: r.item,
        quantity: r.quantity,
        unitCost: r.unitCost,
        totalItemCost: r.totalItemCost,
      }))
    );
  };

  // Share/unshare an existing saved CSV by its filename (label) + projectId
  const toggleShareByFilename = async (fileName, shared) => {
    if (!fileName) return;
    setShareBusy(true);
    try {
      await api.post('/estimates/share-by-filename', {
        fileName,        // e.g. "Estep_Residence_Complete_Estimate.csv"
        projectId,       // from URL /projects/:projectId/estimate
        shared           // true = share to company, false = make private
      });
      bumpSaved();
      toast.success(shared ? 'Shared with your company.' : 'Set to private.');
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Could not change sharing.';
      console.error('[share-by-filename]', msg);
      toast.error(msg);
    } finally {
      setShareBusy(false);
    }
  };

  // design tokens
  const card = 'glass-card';
  const action = 'glass-ghost px-3 py-2 text-xs active:translate-y-px disabled:opacity-60';

  return (
    <div className="min-h-screen text-zinc-200">
      <div className="mx-auto max-w-6xl px-6 py-10">
        {/* Title */}
        <h1 className="mb-8 text-3xl font-semibold tracking-tight">
          <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">
            Estimate
          </span>
          <span className="ml-2 text-zinc-400">
            {activeProject?.name ? `· ${activeProject.name}` : '· Select a project'}
          </span>
        </h1>

        {/* 12-col grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Left: Upload (5/12) */}
          <section className={`${card} p-6 lg:col-span-5`}>
            <div className="mb-4 text-sm uppercase tracking-wide text-zinc-400">Upload Parts List</div>
            <PartsUploader onReady={handlePartsReady} onManual={beginManualEntry} />
            {uploadLabel && (
              <div className="mt-3 text-xs text-zinc-400">Loaded: {uploadLabel}</div>
            )}
          </section>

          {/* Right: Saved (7/12) */}
          <section className={`${card} p-6 lg:col-span-7 lg:col-start-6`}>
            <div className="mb-2 text-sm uppercase tracking-wide text-zinc-400">Saved Estimates</div>
            <p className="mb-3 text-xs text-zinc-500">
              Share a saved estimate with your company without re-uploading.
            </p>

            {/* TableManager should call onShare/onUnshare with the file’s label/filename */}
            <TableManager
              key={savedRefresh}
              onLoad={handleLoad}
              onShare={(fileName)   => toggleShareByFilename(fileName, true)}
              onUnshare={(fileName) => toggleShareByFilename(fileName, false)}
              projectId={projectId} // in case TableManager needs it
            />
          </section>

          {/* New estimate (FULL width under both cards) */}
          {( (manualMode || partsList || uploadedFile) && !loadedTable ) && (
            <section className={`${card} p-6 lg:col-span-12`}>
              <div className="mb-4 text-sm uppercase tracking-wide text-zinc-400">New Estimate</div>
              <CostEstimator
                uploadedFile={uploadedFile}     // <-- pass the real file (CSV or XLSX)
                partsList={partsList || []}     // CSV convenience; XLSX can be []
                tableLabel={uploadLabel}
                manualMode={manualMode}
                manualTrigger={manualTrigger}
                laborProfiles={laborProfiles}
                laborEnabled={laborEnabled}
                laborLoading={laborLoading}
                laborError={laborError}
              />
            </section>
          )}

          {/* Loaded estimate (FULL width under both cards) */}
          {loadedTable && (
            <section className={`${card} p-6 lg:col-span-12`}>
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm uppercase tracking-wide text-zinc-400">Loaded Estimate</div>
                <div className="flex items-center gap-2">
                  <button
                    className={action}
                    onClick={() => toggleShareByFilename(loadedTable.label, true)}
                    disabled={shareBusy}
                    title="Share this estimate with your company"
                  >
                    Share
                  </button>
                  <button
                    className={action}
                    onClick={() => toggleShareByFilename(loadedTable.label, false)}
                    disabled={shareBusy}
                    title="Make this estimate private"
                  >
                    Unshare
                  </button>
                </div>
              </div>

              <CostEstimator
                partsList={partsList}
                tableLabel={loadedTable.label}
                initialResult={{
                  breakdown: loadedTable.breakdown,
                  totalCost: loadedTable.totalCost,
                  materialTotal: loadedTable.materialTotal,
                  laborTotal: loadedTable.laborTotal,
                  laborEntries: loadedTable.laborEntries,
                }}
                laborProfiles={laborProfiles}
                laborEnabled={laborEnabled}
                laborLoading={laborLoading}
                laborError={laborError}
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
